import axiosInstance from "./axiosInstance";
import { parseJwt } from "../utils/authUtils";
import useAuthStore from "../../stores/authStore";

const LOGIN_URL = "/auth/login";
const REGISTER_URL = "/auth/register";
const ACTIVATE_URL = "/auth/activate-account";
const TOKEN_VALID_URL = "/auth/token-valid";
const VERIFY_MFA_URL = "/auth/verify";
const REQUEST_RESET_URL = "/auth/request-password-reset";
const RESET_PASSWORD_URL = "/auth/reset-password";
const RESEND_OTP_URL = "/auth/resend-otp";
const REQUEST_REACTIVATION_URL = "/auth/request-activate-account";
const VERIFY_PIN_URL = "/auth/verify-pin";
const ENROLL_PIN_URL = "/auth/pin/setup";
const PIN_FORGOT_URL = "/auth/pin/forgot";
const PIN_RESET_URL = "/auth/pin/reset";

const authService = {
  login: async (email, password) => {
    return await axiosInstance.post(LOGIN_URL, { email, password });
  },

  // REGISTER
  register: async (userData) => {
    const response = await axiosInstance.post(REGISTER_URL, userData);
    return response.data; // Returns something like "Activation email sent"
  },

  activateAccount: async (token, payload) => {
    return await axiosInstance.post(
      `${ACTIVATE_URL}?token=${encodeURIComponent(token)}`,
      payload,
    );
  },

  checkTokenValid: async (token) => {
    return await axiosInstance.get(
      `${TOKEN_VALID_URL}?token=${encodeURIComponent(token)}`,
    );
  },

  verifyMfaCode: async (code) => {
    const response = await axiosInstance.post(
      `${VERIFY_MFA_URL}?token=${encodeURIComponent(code)}`,
    );
    const { accessToken, refreshToken, remarks } = response.data;
    if (accessToken && refreshToken) {
      useAuthStore.getState().setTokens(accessToken, refreshToken);
      const user = parseJwt(accessToken);
      return { success: true, user, remarks, accessToken, refreshToken };
    } else {
      throw new Error(remarks || "MFA verification failed.");
    }
  },

  resendMfaCode: async (email) => {
    return await axiosInstance.post(
      `${RESEND_OTP_URL}?email=${encodeURIComponent(email)}`,
    );
  },

  requestPasswordReset: async (email) => {
    return await axiosInstance.post(
      REQUEST_RESET_URL,
      {},
      { params: { email } },
    );
  },

  resetPassword: async (token, newPassword) => {
    return await axiosInstance.post(
      `${RESET_PASSWORD_URL}?token=${encodeURIComponent(token)}`,
      { password: newPassword },
    );
  },

  requestReactivation: async (email) => {
    return await axiosInstance.post(REQUEST_REACTIVATION_URL, { email });
  },

  verifyPin: async (challengeId, pin) => {
    return await axiosInstance.post(VERIFY_PIN_URL, { challengeId, pin });
  },

  enrollPinWithChallenge: async (challengeId, newPin, confirmPin) => {
    return await axiosInstance.post(ENROLL_PIN_URL, {
      challengeId,
      newPin,
      confirmPin,
    });
  },

  requestPinReset: async (email) => {
    return await axiosInstance.post(PIN_FORGOT_URL, { email });
  },

  resetPin: async (token, newPin, confirmPin) => {
    return await axiosInstance.post(
      `${PIN_RESET_URL}?token=${encodeURIComponent(token)}`,
      { newPin, confirmPin },
    );
  },

  logout: () => {
    useAuthStore.getState().clearAuth();
    // Optionally notify backend to invalidate session
  },

  isAuthenticated: () => {
    return useAuthStore.getState().isAuthenticated;
  },
};

export default authService;
